import { useCallback, useState } from 'react';

export type FieldErrors = Record<string, string>;

/**
 * Errores por campo para formularios con campos obligatorios.
 *
 * Uso con Fluent `<Field>`:
 *   const fe = useFieldErrors();
 *   <Field label="Fecha" {...fe.props('date')}>
 *     <Input onChange={(_, d) => { setDate(d.value); fe.clear('date'); }} />
 *   </Field>
 *
 * `fe.props(key)` devuelve `validationState="error"` + `validationMessage`, con lo que
 * Fluent pinta el control en rojo y muestra el mensaje DEBAJO del control.
 * `fe.validate({...})` recibe `{ campo: mensaje | falsy }`; guarda los errores y
 * devuelve `true` si todo es válido.
 */
export function useFieldErrors() {
  const [errors, setErrors] = useState<FieldErrors>({});

  const validate = useCallback((checks: Record<string, string | false | null | undefined>): boolean => {
    const next: FieldErrors = {};
    for (const [key, msg] of Object.entries(checks)) {
      if (msg) next[key] = msg;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, []);

  const clear = useCallback((key?: string) => {
    setErrors((prev) => {
      if (key === undefined) return Object.keys(prev).length ? {} : prev;
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const props = useCallback((key: string) => (
    errors[key]
      ? { validationState: 'error' as const, validationMessage: errors[key] }
      : {}
  ), [errors]);

  return { errors, validate, clear, props, hasErrors: Object.keys(errors).length > 0 };
}
